import React from 'react';
import uniqueId from 'lodash/uniqueId';
import {
  Grid,
  Typography,
  makeStyles,
  createStyles,
  Table,
  TableRow,
  TableCell,
  TableBody,
  Link,
  Theme,
  Dialog,
  IconButton,
  useMediaQuery,
  useTheme,
  DialogTitle,
  DialogContent,
} from '@material-ui/core';
import { XCircle } from 'react-feather';
import clsx from 'clsx';
import { ClassNameMap } from '@material-ui/core/styles/withStyles';

import {
  endorsementTypeIndex,
  formatCase,
  generateContent,
} from './endorsementComparisonFunctions';
import { CoverageCatalog } from '../../../types';
import { colors } from '../../../theme';
import { greenCheck } from '../../../svg';
import { formatMoney } from '../../../functions/moneyFormat';
import CarRentalElement from './CarRentalElement';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dynamicTextColor: {
      color: colors.white,
      [theme.breakpoints.down('sm')]: {
        color: colors.madison,
      },
    },
    appBar: {
      textAlign: 'center',
      backgroundColor: colors.madison,
      height: 50,
      minHeight: 50,
      width: '100%',
      [theme.breakpoints.down('sm')]: {
        position: 'sticky',
        backgroundColor: colors.white,
      },
    },
    container: {
      width: '100%',
      margin: 0,
      backgroundColor: colors.white,
    },
    styledRow: {
      '&:nth-of-type(even)': {
        backgroundColor: colors.gallery,
      },
      '&:first-child': {
        backgroundColor: '#F9FAFB',
      },
    },
    topAlignCell: {
      verticalAlign: 'top',
    },
    noPaddingGrid: {
      paddingLeft: '0 !important',
      paddingRight: '0 !important',
    },
    check: { padding: 4, paddingBottom: 6 },
    visibleCheck: { visibility: 'visible' },
    hiddenCheck: { visibility: 'hidden' },
    link: { color: colors.bright_turquoise },
    table: {
      tableLayout: 'fixed',
    },
    dynamicCell: {
      width: '100%',
      [theme.breakpoints.down('sm')]: {
        padding: '8px',
      },
    },
    fixedCell: {
      width: '100px',
      [theme.breakpoints.down('sm')]: {
        width: '84px',
        padding: '8px',
      },
    },
    firstCell: {
      width: 0,
      padding: 0,
      [theme.breakpoints.up('sm')]: {
        width: 32,
      },
    },
    lastCell: {
      width: 0,
      padding: 0,
      [theme.breakpoints.up('sm')]: {
        width: 32,
      },
    },
    closeIcon: {
      position: 'absolute',
      top: 14,
      right: theme.spacing(2),
      zIndex: 1101,
      [theme.breakpoints.down('sm')]: {
        position: 'fixed',
      },
    },
    iconButton: {
      color: colors.white,
      [theme.breakpoints.down('sm')]: {
        color: colors.madison,
      },
    },
    dialogue: {
      borderRadius: 10,
      [theme.breakpoints.down('md')]: {
        borderRadius: 0,
      },
    },
  }),
);

/**
 * Instead of typing a lot of JSX, we will just cycle through this multi-dimensional
 * array to build the table's content.
 * Note: any value represented by "X" will be replaced by an XCircle icon.
 */

const tableSchema: Array<Array<React.ReactNode>> = [
  [
    'Cobertura de deducible: Exoneración de deducible aún cuando eres responsable',
    'Incluido',
    'X',
    'X',
  ],
  [
    'Exoneración de deducible en sitio cuando eres inocente',
    'Aplica la cobertura de deducible',
    '100%',
    '100%',
  ],
  [
    'Descuento en deducible para mujeres',
    'Aplica la cobertura de deducible',
    '50%',
    '50%',
  ],
  [
    'Descuento de deducible por buen historial (hombres)',
    '$100 por año',
    '$100 por año',
    'X',
  ],
  ['CarRentalElement'],
  [
    'No depreciación de piezas por accidente parcial',
    '0-10 años',
    '0-3 años',
    '0-3 años',
  ],
  [
    'No depreciación por pérdida total en el primer año del auto',
    'Sí',
    'Sí',
    'Sí',
  ],
  ['Robo de artículos personales', '$1,000', '$500', '$100'],
  [
    '3 atenciones de grúa al año',
    '2 traslados por atención',
    '1 traslado por atención',
    '1 traslado por atención',
  ],
  [
    'Cobertura en todo Centroamérica (excepto Belice)',
    '30 días',
    '30 días',
    '30 días',
  ],
  [
    'Reembolso hasta el 100% de la deuda pendiente al acreedor (Gap Insurance)',
    'Sí',
    'Sí',
    'X',
  ],
  ['Responsabilidad civil por uso de remolque o trailer', 'Sí', 'Sí', 'X'],
  ['ASSA Pet (gastos médicos, robo y otros para tu mascota)', 'Sí', 'X', 'X'],
  ['Muerte accidental de conductor', '$15,000', '$15,000', '$10,000'],
  ['Gastos funerarios', '$3,000', '$3,000', '$1,500'],
];

export const platinoPlusDescription = '(Exoneración del deducible)';

// renders the table column
export function renderDataRow(
  endorsementsIndex: Array<number>,
  curSchema: Array<React.ReactNode>,
  classes: ClassNameMap,
): React.ReactNode {
  const [description] = curSchema;
  const columnNumber = endorsementsIndex.length + 1;

  if (description === 'CarRentalElement') {
    return (
      <TableCell colSpan={columnNumber} style={{ paddingRight: 0 }}>
        <CarRentalElement endorsementsIndex={endorsementsIndex} />
      </TableCell>
    );
  }

  return (
    <>
      <TableCell className={classes.dynamicCell}>
        <Typography variant="caption">{description}</Typography>
      </TableCell>
      {endorsementsIndex.map((index) => {
        if (curSchema.length > index) {
          return (
            <TableCell
              key={uniqueId()}
              className={classes.fixedCell}
              data-testid={curSchema[index]}
            >
              {generateContent(curSchema[index])}
            </TableCell>
          );
        }
        return <TableCell data-testid="invalidDataId" key={uniqueId()} />;
      })}
    </>
  );
}

const ANNUAL_PAYMENT_AMOUNT = 1;

type Props = {
  endorsements: CoverageCatalog[];
  selectedEndorsement: string;
  isOpen: boolean;
  toggleDialogue: (isOpen: boolean) => void;
  paymentAmount?: number;
};

const EndorsementComparison = ({
  endorsements,
  selectedEndorsement,
  isOpen,
  toggleDialogue,
  paymentAmount = ANNUAL_PAYMENT_AMOUNT,
}: Props): JSX.Element => {
  const classes = useStyles();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  /* Retrieving indexes to render table above */
  const endorsementsIndex = endorsementTypeIndex(endorsements);

  function generateTitle(
    name: string,
    active: boolean,
    code: string,
    maxCost: number,
  ): React.ReactNode {
    return (
      <TableCell
        className={clsx(classes.topAlignCell, classes.fixedCell)}
        key={name}
      >
        <Grid
          data-testid={`check-${name}`}
          container
          justifyContent="center"
          className={clsx(
            classes.check,
            active ? classes.visibleCheck : classes.hiddenCheck,
          )}
        >
          <img alt="selected" src={greenCheck} />
        </Grid>
        <Grid container justifyContent="center" direction="column">
          <Typography variant="caption" align="center">
            {name}
          </Typography>
          <Typography
            style={{
              fontSize: '19px',
              fontWeight: 900,
              lineHeight: '24px',
            }}
            data-testid={`coverageCostId-${code}`}
            align="center"
          >
            {maxCost !== 0
              ? `${formatMoney(maxCost / paymentAmount)}`
              : 'Gratis'}
          </Typography>
          <Typography
            variant="caption"
            data-testid={`coverageCostId-${code}-text`}
            align="center"
          >
            {paymentAmount !== ANNUAL_PAYMENT_AMOUNT && 'mensual'}
          </Typography>
        </Grid>
      </TableCell>
    );
  }

  return (
    <Dialog
      data-testid="dialogue"
      open={isOpen}
      onClose={(): void => toggleDialogue(false)}
      fullWidth
      maxWidth="sm"
      fullScreen={isMobile}
      PaperProps={{
        className: classes.dialogue,
      }}
    >
      {/* Title bar (with close logic) */}
      <DialogTitle
        disableTypography
        className={classes.appBar}
        style={{ position: 'sticky', top: '0px' }}
      >
        <Typography className={classes.dynamicTextColor} variant="body1">
          Comparación de beneficios
        </Typography>
        <IconButton
          data-testid="close-button"
          className={classes.closeIcon}
          onClick={(): void => toggleDialogue(false)}
        >
          <XCircle size={24} className={classes.iconButton} />
        </IconButton>
      </DialogTitle>
      <DialogContent style={{ padding: '0px' }}>
        <Grid container spacing={1} className={classes.container}>
          <Grid item xs={12} className={classes.noPaddingGrid}>
            <Table className={classes.table}>
              <TableBody>
                <TableRow
                  className={classes.styledRow}
                  style={{ position: 'sticky', top: '0px' }}
                >
                  {/* Titles */}
                  <TableCell className={classes.firstCell} />
                  <TableCell className={classes.dynamicCell} />
                  {endorsements.map((coverage) =>
                    generateTitle(
                      formatCase(coverage.name),
                      coverage.code === selectedEndorsement,
                      coverage.code,
                      coverage.maxCost,
                    ),
                  )}
                  <TableCell className={classes.lastCell} />
                </TableRow>
                {/* Descriptions */}
                {tableSchema.map(
                  (curSchema: Array<React.ReactNode>): React.ReactNode => (
                    <TableRow className={classes.styledRow} key={uniqueId()}>
                      <TableCell className={classes.firstCell} />
                      {renderDataRow(endorsementsIndex, curSchema, classes)}
                      <TableCell className={classes.lastCell} />
                    </TableRow>
                  ),
                )}
                <TableRow>
                  <TableCell colSpan={6} className={classes.dynamicCell}>
                    <Typography
                      variant="caption"
                      style={{ marginRight: '0.3em' }}
                    >
                      Para mayor información sobre los beneficios adicionales o
                      endosos, visita nuestro sitio web de
                    </Typography>
                    <Typography variant="caption">
                      <Link
                        data-testid="coverage-link"
                        href="https://auto.assanet.com/cobertura-completa/"
                        target="_blank"
                        className={classes.link}
                      >
                        ASSA Seguro de Auto
                      </Link>
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default EndorsementComparison;
